import React, { useState } from 'react';
import { PartnerApplication } from '../types';
import { FileText, Mail, Phone, MapPin, Globe, Building, Users, Target, TrendingUp, Package, Calendar, Clock, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { partnerApplicationService } from '../../../services/partnerApplicationService';

interface ApplicationReviewPanelProps {
  application: PartnerApplication | null;
  onPreApprove: (partnerId: string) => void;
  onApprove: (partnerId: string, commissionRate: number) => void;
  onReject: (partnerId: string, reason: string) => void;
  onAddContractConditions?: (partnerId: string, notes?: string) => void;
  onSendContract?: (partnerId: string) => void;
  onRecordContractSignature?: (partnerId: string) => void;
}

const ApplicationReviewPanel: React.FC<ApplicationReviewPanelProps> = ({ 
  application, 
  onPreApprove, 
  onApprove, 
  onReject,
  onAddContractConditions,
  onSendContract,
  onRecordContractSignature
}) => {
  const [commissionRate, setCommissionRate] = useState<number>(10);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [showPreApprovalModal, setShowPreApprovalModal] = useState<boolean>(false);
  const [showApprovalModal, setShowApprovalModal] = useState<boolean>(false);
  const [showRejectionModal, setShowRejectionModal] = useState<boolean>(false);
  const [isPreApproving, setIsPreApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isSkippingCall, setIsSkippingCall] = useState(false);
  const [contractConditionsNotes, setContractConditionsNotes] = useState('');
  const [showAccountCreationModal, setShowAccountCreationModal] = useState<boolean>(false);
  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const [accountEmail, setAccountEmail] = useState<string>('');
  const [accountFullName, setAccountFullName] = useState<string>('');
  const [accountPhone, setAccountPhone] = useState<string>('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isCompletingCall, setIsCompletingCall] = useState(false);
  const [onboardingCallNotes, setOnboardingCallNotes] = useState('');

  // Determine which action buttons to show based on current stage
  const showPreApprovalActions = application?.partner.status === 'pending';
  const showOnboardingCallAction = 
    application?.partner.status === 'pre_approved' && 
    !application?.partner.onboarding_call_completed;
  const showContractConditionsAction = 
    application?.partner.status === 'pre_approved' && 
    application?.partner.onboarding_call_completed &&
    !application?.partner.contract_conditions_added;
  const showSendContractAction = 
    application?.partner.status === 'pre_approved' && 
    application?.partner.contract_conditions_added &&
    !application?.partner.contract_sent_at;
  const showRecordSignatureAction = 
    application?.partner.status === 'pre_approved' && 
    application?.partner.contract_sent_at &&
    !application?.partner.contract_signed_at;
  const showCreateAccountAction = 
    application?.partner.status === 'pre_approved' && 
    application?.partner.contract_signed_at &&
    !application?.partner.credentials_sent_at;
  const showFinalApprovalActions = 
    application?.partner.status === 'pre_approved' && 
    application?.partner.credentials_sent_at;

  if (!application) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Application Selected</h3>
        <p className="text-sm text-gray-500">Select an application from the queue to begin review</p>
      </div>
    );
  }

  const { partner } = application;
  const isPending = partner.status === 'pending';
  const isPreApproved = partner.status === 'pre_approved';

  const handlePreApprove = async () => {
    if (!application) return;

    setIsPreApproving(true);
    try {
      const result = await partnerApplicationService.preApprovePartner(application.partner.id);
      
      if (result?.success) {
        toast.success('Partner pre-approved successfully - workflow updated');
        onPreApprove(application.partner.id);
      } else {
        toast.error(result?.message || 'Failed to pre-approve partner');
      }
    } catch (error: any) {
      console.error('Pre-approval error:', error);
      toast.error(error?.message || 'Error pre-approving partner application');
    } finally {
      setIsPreApproving(false);
    }
  };

  const handleApproveClick = () => {
    setShowApprovalModal(true);
  };

  const handleRejectClick = () => {
    setShowRejectionModal(true);
  };

  const handleSkipOnboardingCall = async () => {
    if (!application) return;

    setIsSkippingCall(true);
    try {
      const result = await partnerApplicationService.skipOnboardingCall(application.partner.id);
      
      if (result?.success) {
        toast.success('Onboarding call skipped - proceeding to contract signing stage');
        window.location.reload();
      } else {
        toast.error(result?.message || 'Failed to skip onboarding call');
      }
    } catch (error: any) {
      console.error('Skip onboarding call error:', error);
      toast.error(error?.message || 'Error skipping onboarding call');
    } finally {
      setIsSkippingCall(false);
    }
  };

  const handleCompleteOnboardingCall = async () => {
    if (!application) return;

    setIsCompletingCall(true);
    try {
      const result = await partnerApplicationService.completeOnboardingCall(
        application.partner.id,
        onboardingCallNotes || undefined
      );
      
      if (result?.success) {
        toast.success('Onboarding call completed - contract automatically sent to partner');
        setOnboardingCallNotes('');
        window.location.reload();
      } else {
        toast.error(result?.message || 'Failed to complete onboarding call');
      }
    } catch (error: any) {
      console.error('Complete onboarding call error:', error);
      toast.error(error?.message || 'Error completing onboarding call');
    } finally {
      setIsCompletingCall(false);
    }
  };

  const confirmPreApproval = async () => {
    setShowPreApprovalModal(false);
    await handlePreApprove();
  };

  const confirmApproval = async () => {
    try {
      // TODO: Implement actual approval service call
      toast.success('Partner approved successfully');
      setShowApprovalModal(false);
    } catch (error: any) {
      toast.error(error.message || 'Error approving partner');
    }
  };

  const confirmRejection = async () => {
    if (rejectionReason.trim()) {
      try {
        setIsRejecting(true);
        // TODO: Implement actual rejection service call
        toast.success('Partner application rejected');
        setShowRejectionModal(false);
        setRejectionReason('');
      } catch (error: any) {
        toast.error(error.message || 'Error rejecting partner');
      } finally {
        setIsRejecting(false);
      }
    }
  };

  const generatePassword = () => {
    // Generate a random 12-character password with uppercase, lowercase, numbers, and special chars
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    const all = upper + lower + numbers + special;
    
    let password = '';
    // Ensure at least one of each type
    password += upper[Math.floor(Math.random() * upper.length)];
    password += lower[Math.floor(Math.random() * lower.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest
    for (let i = 4; i < 12; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    setGeneratedPassword(password);
  };

  const handleOpenAccountCreation = () => {
    if (!application) return;
    
    // Prepopulate with partner info
    setAccountEmail(application.partner.contact_email);
    setAccountFullName(application.partner.contact_name || application.partner.business_name);
    setAccountPhone(application.partner.contact_phone || '');
    
    // Generate initial password
    generatePassword();
    
    setShowAccountCreationModal(true);
  };

  const handleCreateAccount = async () => {
    if (!application || !accountEmail) return;

    setIsCreatingAccount(true);
    try {
      // 🔥 CRITICAL CHANGE: Always use "algernon99" as initial password - user never sees this
      // Account is created with password "algernon99" and user immediately receives reset email
      const result = await partnerApplicationService.createPartnerAccountWithAuth(
        application.partner.id,
        accountEmail,
        accountFullName,
        accountPhone,
        'algernon99' // Fixed initial password (never revealed to partner)
      );
      
      if (result?.success) {
        toast.success('✅ Password reset email sent! Partner can now set their own password.');
        setShowAccountCreationModal(false);
        // Refresh the application list
        window.location.reload();
      } else {
        toast.error(result?.message || 'Failed to create account');
      }
    } catch (error: any) {
      console.error('Account creation error:', error);
      toast.error(error?.message || 'Error creating partner account');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Password copied to clipboard');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm flex flex-col h-[calc(100vh-12rem)] max-h-[1500px]">
      {/* Header - Fixed at top */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-lg flex-shrink-0">
        <h2 className="text-xl font-bold text-white">Application Review</h2>
        <p className="text-blue-100 text-sm mt-1">Review partner details and documentation</p>
      </div>

      {/* Scrollable Content - Takes remaining space */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {/* Business Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2 text-blue-600" />
            Business Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Business Name</label>
              <p className="text-gray-900 font-medium mt-1 break-words">{partner.business_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Business Type</label>
              <p className="text-gray-900 mt-1">{partner.business_type || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Establishment Size</label>
              <p className="text-gray-900 mt-1">{partner.establishment_size || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">License Number</label>
              <p className="text-gray-900 mt-1 break-words">{partner.business_license_number || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Tax ID</label>
              <p className="text-gray-900 mt-1 break-words">{partner.tax_id || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">VAT Number</label>
              <p className="text-gray-900 mt-1 break-words">{partner.vat_number || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Mail className="w-5 h-5 mr-2 text-blue-600" />
            Contact Information
          </h3>
          <div className="space-y-3">
            {partner.contact_name && (
              <div className="flex items-start">
                <Users className="w-4 h-4 mr-3 text-gray-400 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 block">Contact Name</label>
                  <span className="text-gray-900 break-words">{partner.contact_name}</span>
                </div>
              </div>
            )}
            <div className="flex items-start">
              <Mail className="w-4 h-4 mr-3 text-gray-400 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500 block">Email</label>
                <span className="text-gray-900 break-words">{partner.contact_email}</span>
              </div>
            </div>
            {partner.contact_phone && (
              <div className="flex items-start">
                <Phone className="w-4 h-4 mr-3 text-gray-400 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 block">Phone</label>
                  <span className="text-gray-900 break-words">{partner.contact_phone}</span>
                </div>
              </div>
            )}
            {partner.business_address && (
              <div className="flex items-start">
                <MapPin className="w-4 h-4 mr-3 text-gray-400 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 block">Address</label>
                  <span className="text-gray-900 break-words">{partner.business_address}</span>
                </div>
              </div>
            )}
            {partner.website_url && (
              <div className="flex items-start">
                <Globe className="w-4 h-4 mr-3 text-gray-400 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 block">Website</label>
                  <a 
                    href={partner.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline break-words"
                  >
                    {partner.website_url}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Social Media Links */}
        {partner.social_media_urls && Object.keys(partner.social_media_urls).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Globe className="w-5 h-5 mr-2 text-blue-600" />
              Social Media
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(partner.social_media_urls).map(([platform, url]) => (
                url && (
                  <div key={platform} className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500 capitalize">{platform}:</span>
                    <a 
                      href={url as string} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-blue-600 hover:underline truncate flex-1"
                    >
                      {url as string}
                    </a>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* Business Description */}
        {partner.business_description && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Business Description
            </h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words bg-gray-50 p-4 rounded-lg">
              {partner.business_description}
            </p>
          </div>
        )}

        {/* Target Demographics */}
        {partner.target_demographics && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <Target className="w-5 h-5 mr-2 text-blue-600" />
              Target Demographics
            </h3>
            <p className="text-gray-700 leading-relaxed break-words bg-gray-50 p-4 rounded-lg">
              {partner.target_demographics}
            </p>
          </div>
        )}

        {/* Marketing Channels */}
        {partner.current_marketing_channels && partner.current_marketing_channels.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
              Current Marketing Channels
            </h3>
            <div className="flex flex-wrap gap-2">
              {partner.current_marketing_channels.map((channel, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                >
                  {channel}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Desired Offer Types */}
        {partner.desired_offer_types && partner.desired_offer_types.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Package className="w-5 h-5 mr-2 text-blue-600" />
              Desired Offer Types
            </h3>
            <div className="flex flex-wrap gap-2">
              {partner.desired_offer_types.map((type, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Expected Customer Volume */}
        {partner.expected_customer_volume && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              Expected Monthly Customer Volume
            </h3>
            <p className="text-gray-700 font-medium text-lg">
              {partner.expected_customer_volume}
            </p>
          </div>
        )}

        {/* Marketing Goals */}
        {partner.marketing_goals && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
              Marketing Goals
            </h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words bg-gray-50 p-4 rounded-lg">
              {partner.marketing_goals}
            </p>
          </div>
        )}

        {/* Additional Notes */}
        {partner.applicant_notes && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Additional Notes
            </h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words bg-gray-50 p-4 rounded-lg">
              {partner.applicant_notes}
            </p>
          </div>
        )}

        {/* Application Metrics */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-blue-600" />
            Application Metrics
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Submission Date</p>
              <p className="text-gray-900 font-medium mt-1">
                {new Date(partner.application_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Existing Offers</p>
              <p className="text-gray-900 font-medium mt-1">{application.offers_count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Partnership Tier</p>
              <p className="text-gray-900 font-medium mt-1 capitalize">{partner.partnership_tier}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Approval Modal */}
      {showPreApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="w-6 h-6 mr-2 text-blue-600" />
                Pre-Approve Partner Application
              </h3>
              <div className="space-y-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  Pre-approving this application will:
                </p>
                <ul className="text-sm text-gray-700 space-y-2 ml-4">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Send a calendar scheduling link to <strong>{partner.contact_email}</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Allow the partner to select a 45-minute timeslot for an onboarding call</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Move the application to the next stage of the workflow</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mt-4">
                  After the call is completed and contract is signed, you can proceed with final approval.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowPreApprovalModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPreApproval}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Confirm Pre-Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Approve Partner Application</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission Rate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Approving this application will create a partner account and grant access to the partner dashboard.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApproval}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Confirm Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Reject Partner Application</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Provide a detailed reason for rejection..."
                  />
                </div>
                <p className="text-sm text-gray-600">
                  The partner will be notified via email with the rejection reason.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRejection}
                  disabled={!rejectionReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Creation Modal */}
      {showAccountCreationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <UserPlus className="w-6 h-6 mr-2 text-blue-600" />
                Create Partner Account
              </h3>
              
              <div className="space-y-4">
                {/* Prepopulated Partner Info */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 mb-2">Partner Information</p>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p><strong>Business:</strong> {application?.partner.business_name}</p>
                    <p><strong>Code:</strong> {application?.partner.partner_code}</p>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="partner@example.com"
                  />
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={accountFullName}
                    onChange={(e) => setAccountFullName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Doe"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={accountPhone}
                    onChange={(e) => setAccountPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1234567890"
                  />
                </div>

                {/* 🔥 REMOVED: Password generation UI - user never sees the initial password */}
                
                {/* 🔥 UPDATED: Important Note about Password Reset Workflow */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-900 mb-2">Automatic Password Setup Process</p>
                      <ul className="text-sm text-green-800 space-y-1.5">
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">1.</span>
                          <span>System creates account with initial password "algernon99" (never shared with partner)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">2.</span>
                          <span><strong>Supabase automatically sends password reset email</strong> to {accountEmail || 'partner email'}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">3.</span>
                          <span>Partner clicks the reset link and creates their own secure password</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">4.</span>
                          <span>Partner logs in with their new password immediately</span>
                        </li>
                      </ul>
                      <p className="text-xs text-green-700 mt-3 italic bg-green-100 p-2 rounded">
                        🔒 Security: Partner never sees "algernon99" - they set their own password from the start
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAccountCreationModal(false);
                    setGeneratedPassword('');
                  }}
                  disabled={isCreatingAccount}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAccount}
                  disabled={!accountEmail || !accountFullName || isCreatingAccount}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-md"
                >
                  {isCreatingAccount ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationReviewPanel;